import { useState } from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Link,
  Typography,
} from '@mui/material';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import Test from 'react-test-attributes';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { createSessionStripe, redirectCheckout } from 'src/services/portal';
import { cancelSubscription } from 'src/services/subscription';
import { Plan, PlanDescription, PlanPrice } from 'src/types/plans';

import ConfirmationDialog from '../ConfirmationDialog';
import { If } from '../If';
import { UsersCounter } from '../UserCounter';

type PlanCardProps = {
  plan: Plan;
  period: string | null;
  buttonLabel: string;
  isDowngrade: boolean;
  handleSetSubscribers?: (value: number) => void;
};

export function PlanCard({
  plan,
  period,
  buttonLabel,
  isDowngrade = false,
  handleSetSubscribers,
}: PlanCardProps) {
  const history = useHistory();
  const { auth, reloadUser } = useAuthContext();
  const [userCounter, setUserCounter] = useState<number>(
    plan?.minSubscriptions,
  );
  const planPrice = plan?.prices?.find(
    (price: PlanPrice) => price.interval === period,
  );

  const [openDialog, setOpenDialog] = useState(false);

  const cancelSubscriptionMutation = useMutation(() => cancelSubscription(), {
    onSuccess: async () => {
      history.push('/plataforma');
      reloadUser();
    },
  });

  const createSession = useMutation('createSession', createSessionStripe, {
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noreferrer');
    },
  });

  const checkoutMutation = useMutation('redirectCheckout', redirectCheckout, {
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noreferrer');
    },
  });

  const getTooltipText = (price?: PlanPrice) => {
    if (
      auth.user?.nextSubscription &&
      (!price || price?.priceId !== auth?.user?.nextSubscription?.priceId)
    ) {
      return 'Você possui uma assinatura pendente';
    } else if (price && price?.priceId === auth?.user?.subscription?.priceId) {
      return 'Plano atual';
    }
    return '';
  };

  const getFrequency = (period: string) => {
    switch (period) {
      case 'month':
        return '(Plano Mensal)';
      case 'year':
        return '(Plano Anual)';
      default:
        return '(Plano Mensal)';
    }
  };

  const getPlanHierarchy = (planType: string): number => {
    const hierarchy: Record<string, number> = {
      free: 0,
      demo: 1,
      team: 2,
      vip: 3,
    };
    return hierarchy[planType] ?? 0;
  };

  const isDowngradeChange = (): boolean => {
    if (!auth.user?.subscription) return false;

    const currentPlanType = auth.user.subscription.plan.type;
    const currentInterval = auth.user.subscription?.plan?.prices?.find(
      (price) => price.priceId === auth.user?.subscription?.priceId,
    )?.interval;
    const currentSubscribers = auth.user.subscription.slots || 1;

    const targetPlanType = plan.type;
    const targetInterval = period;
    const targetSubscribers = userCounter;

    if (!auth.isLoggedIn || ['free', 'demo'].includes(currentPlanType)) {
      return false;
    }

    if (currentInterval === 'year' && targetInterval === 'month') {
      return true;
    }

    const currentHierarchy = getPlanHierarchy(currentPlanType);
    const targetHierarchy = getPlanHierarchy(targetPlanType);

    if (targetHierarchy < currentHierarchy) {
      return true;
    }

    if (
      currentPlanType === targetPlanType &&
      currentInterval === targetInterval &&
      targetSubscribers < currentSubscribers
    ) {
      return true;
    }

    return false;
  };

  const handleClick = () => {
    if (!auth.isLoggedIn) {
      history.push('/cadastro', {
        planPrice,
        planCount: ['team', 'vip'].includes(plan.type) ? userCounter : 1,
      });
      return;
    }

    if (plan.type === 'free' && auth.user?.subscription.plan.type === 'demo') {
      setOpenDialog(true);
      return;
    }

    if (
      auth.user?.nextSubscription &&
      auth.user?.nextSubscription?.plan?.type === plan?.type
    ) {
      createSession.mutate({ type: 'customer_portal' });
      return;
    }

    if (
      ['free', 'demo'].includes(auth.user?.subscription?.plan?.type ?? '') ||
      !auth.user?.isOwner
    ) {
      checkoutMutation.mutate({
        priceId: planPrice?.priceId,
        quantity: ['team', 'vip'].includes(plan.type) ? userCounter : 1,
      });
      return;
    }

    const currentPlanType = auth.user?.subscription?.plan?.type;
    const isUpgradeBetweenDifferentPlans = currentPlanType !== plan.type;

    if (isUpgradeBetweenDifferentPlans) {
      createSession.mutate({ type: 'customer_portal' });
      return;
    }

    createSession.mutate({ type: 'subscription_update' });
  };

  const handleMailToLink = () => {
    const subject = encodeURIComponent('Troca de Assinatura');
    const body =
      encodeURIComponent(`Nome do Cliente: ${auth.user?.name || 'Não disponível'}
E-mail: ${auth.user?.email || 'Não disponível'}
Código da Assinatura: ${auth.user?.subscription?.id || 'Não disponível'}

Olá, Gostaria de saber mais sobre a troca de assinatura referente ao:

Plano: ${plan.name}
Frequência: ${getFrequency(period ?? '')}
${plan?.type === 'team' ? `Número de Usuários: ${userCounter}` : ''}

Aguardo retorno.
Obrigado!`);

    window.open(
      `mailto:comercial@uqbar.com.br?subject=${subject}&body=${body}`,
      '_blank',
      'noreferrer',
    );
  };

  const handleSetUserCounter = (value: number) => {
    if (handleSetSubscribers) {
      handleSetSubscribers(value);
    }
    setUserCounter(value);
  };

  const handleDisableButton = (price: PlanPrice) => {
    if (
      auth?.user?.nextSubscription &&
      price?.priceId !== auth?.user?.nextSubscription?.priceId
    ) {
      return true;
    }

    if (price?.priceId === auth?.user?.subscription?.priceId) {
      if (['team', 'vip'].includes(plan.type)) {
        return userCounter === auth.user?.subscription?.slots;
      }
      return true;
    }

    return false;
  };

  return (
    <Card
      elevation={plan.type === 'free' ? 2 : plan.type === 'vip' ? 8 : 4}
      sx={{
        minHeight: 500,
        height: '100%',
        padding: '24px 20px 0px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background:
          plan.type === 'free'
            ? 'white'
            : plan.type === 'vip'
              ? 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)'
              : '#F1F1F1',
        borderRadius: '10px',
        border:
          plan.type === 'vip' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
        color: plan.type === 'vip' ? 'white' : 'inherit',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
        },
      }}>
      <CardContent sx={{ padding: 0 }}>
        <Typography
          sx={{ fontSize: '19.15px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {plan.name}
        </Typography>
        {plan?.type === 'free' && (
          <Box sx={{ minHeight: 84 }}>
            <Box display="flex" alignItems="baseline" justifyContent="center">
              <Typography sx={{ fontSize: '22px' }}>R$</Typography>
              <Typography
                sx={{
                  fontSize: '44px',
                  fontWeight: 700,
                  padding: '0 0.5rem',
                }}>
                {(0).toLocaleString('pt-br')}
              </Typography>
            </Box>
          </Box>
        )}
        {plan?.prices
          ?.filter((price: PlanPrice) => price.interval === period)
          ?.map((price: PlanPrice) => {
            let planPrice = price?.price / 100;
            const planCount = userCounter - plan.minSubscriptions;

            if (price.unit_price) {
              planPrice = (price.price + price.unit_price * planCount) / 100;
            }

            return (
              <Box key={price.id} sx={{ minHeight: 84 }}>
                <Box
                  display="flex"
                  alignItems="baseline"
                  justifyContent="center">
                  <Typography sx={{ fontSize: '22px' }}>R$</Typography>
                  <Typography
                    sx={{
                      fontSize: '44px',
                      fontWeight: 700,
                      padding: '0 0.5rem',
                    }}>
                    {planPrice.toLocaleString('pt-br')}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="end"
                  alignItems="flex-end"
                  marginTop="-8px">
                  <Typography sx={{ fontSize: '12px' }}>
                    {['team', 'vip'].includes(plan.type)
                      ? getFrequency('year')
                      : getFrequency(price.interval)}
                  </Typography>
                </Box>
                {price.unit_price && (
                  <Box display="flex" justifyContent="center" marginTop="16px">
                    <Typography sx={{ fontSize: '10px' }}>{`+ R$ ${
                      price.unit_price / 100
                    } por usuário`}</Typography>
                  </Box>
                )}
                {['team', 'vip'].includes(plan.type) && (
                  <Box display="flex" justifyContent="center" marginTop="8px">
                    <UsersCounter
                      planType={plan.type}
                      max={plan?.maxSubscriptions}
                      min={plan?.minSubscriptions}
                      userCounter={userCounter}
                      handleSetUserCounter={handleSetUserCounter}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        {plan.descriptions.map((description: PlanDescription) => (
          <Box
            key={description.id}
            display="flex"
            alignItems="baseline"
            mt={1}
            justifyContent="flex-start">
            <CheckCircleRoundedIcon
              color={
                plan.type === 'free'
                  ? 'success'
                  : plan.type === 'vip'
                    ? 'primary'
                    : 'secondary'
              }
              sx={{ paddingTop: '10px' }}
            />
            <Typography
              sx={{
                fontSize: '14.37px',
                marginLeft: '8px',
              }}>
              {description.value}
            </Typography>
          </Box>
        ))}
      </CardContent>
      <CardActions
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%',
          }}>
          <>
            {plan.type === 'free' && (
              <CustomTooltip placement="bottom" title={getTooltipText()}>
                <span style={{ width: '100%' }}>
                  {/* @ts-ignore */}
                  <Test id={'plans-button-subscrive' + plan.name}>
                    <Button
                      variant="contained"
                      sx={{
                        fontWeight: 700,
                        marginBottom: 1,
                        width: '100%',
                        backgroundColor: '#FF8211',
                        '&:hover': {
                          backgroundColor: '#FF8211',
                        },
                      }}
                      disabled={!!auth.user?.nextSubscription}
                      onClick={handleClick}>
                      {buttonLabel}
                    </Button>
                  </Test>
                </span>
              </CustomTooltip>
            )}
            {plan.type !== 'free' &&
              plan?.prices
                ?.filter((price: PlanPrice) => price.interval === period)
                ?.map((price, index) => (
                  <CustomTooltip
                    key={index}
                    placement="bottom"
                    title={getTooltipText(price)}>
                    <span style={{ width: '100%' }}>
                      {/* @ts-ignore */}
                      <Test id={'plans-button-subscrive' + plan.name}>
                        <Button
                          variant="contained"
                          sx={(theme) => ({
                            fontWeight: 700,
                            marginBottom: 1,
                            width: '100%',
                            backgroundColor:
                              isDowngradeChange() && plan.type !== 'vip'
                                ? theme.palette.secondary.main
                                : plan.type === 'vip'
                                  ? theme.palette.primary.main
                                  : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor:
                                isDowngradeChange() && plan.type !== 'vip'
                                  ? theme.palette.secondary.main
                                  : plan.type === 'vip'
                                    ? theme.palette.primary.main
                                    : theme.palette.primary.main,
                            },
                          })}
                          onClick={
                            isDowngradeChange() ? handleMailToLink : handleClick
                          }
                          disabled={handleDisableButton(price)}>
                          {buttonLabel}
                        </Button>
                      </Test>
                    </span>
                  </CustomTooltip>
                ))}
          </>
          <Link
            sx={{
              textAlign: 'center',
              margin: '16px 0',
              cursor: 'pointer',
            }}
            onClick={() =>
              plan.detailsLink &&
              window.open(plan.detailsLink, '_blank', 'noopener noreferrer')
            }>
            Saiba mais
          </Link>
        </Box>
      </CardActions>
      <If condition={plan.type === 'free' && openDialog}>
        <ConfirmationDialog
          title={
            auth.user?.subscription.status !== 'Expirado'
              ? 'Você está no período de demonstração'
              : 'Seu período de demonstração expirou'
          }
          subTitle="Ao assinar o plano gratuito, você tem acesso a melhor plataforma do mercado de finanças estruturadas, deseja assinar o plano gratuito agora?"
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          handleAction={() => cancelSubscriptionMutation.mutate()}
        />
      </If>
    </Card>
  );
}
